import React from "react";
import { Checkbox, FlatButton } from "material-ui";
import ClickToSelect from "@mapbox/react-click-to-select";
import ColorPicker from "rc-color-picker";
import Colr from "colr";

import BaseOverlay from "./partials/overlays/base.jsx";
import WorkingIndicator from "./partials/working-indicator.jsx";

import Label from "./partials/label.jsx";
import SelectField from "./partials/select-field.jsx";
import InfoNotice from "./partials/info-notice.jsx";

import connectToStores from "alt-utils/lib/connectToStores";
import EventBucketActions from "../actions/EventBucketActions";
import EventBucketStore from "../stores/EventBucketStore";

const EMBED_PAGES = [
  { label: "Schedule", value: "schedule", inlineLabel: "schedule" },
  { label: "Pass sales", value: "passes/buy", inlineLabel: "pass sales" },
  { label: "Membership sales", value: "memberships/buy", inlineLabel: "membership sales" },
  { label: "Film guide", value: "films", inlineLabel: "film guide" },
  { label: "Welcome page", value: "welcome", inlineLabel: "site" },
  { label: "Donation page", value: "donate", inlineLabel: "donation page" }
];

const VIEW_MODES = [
  { label: "Compact view", value: "compact" },
  { label: "Extended view", value: "extended" },
  { label: "Timeline view", value: "timeline" },
  { label: "Cover view", value: "cover" },
  { label: "Grid view", value: "grid" }
];

class EventBucketEmbed extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      defaultViewMode: VIEW_MODES[0].value,
      defaultPage: EMBED_PAGES[0].value,
      color: "#ffffff",
      invertTextColor: false,
      showCustomContent: false,
      alpha: 0,
      colr: new Colr()
    };

    this.close = this.close.bind(this);
  }

  static getStores() {
    return [EventBucketStore];
  }

  static getPropsFromStores() {
    return {
      getEventBucketById: EventBucketStore.getEventBucketById
    };
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object
    };
  }

  getStyles() {
    return {
      pre: {
        wordWrap: "break-word",
        whiteSpace: "pre-wrap",
        padding: "10px",
        border: "1px solid #ccc",
        borderRadius: "5px",
        fontFamily: "monospace",
        marginTop: "1em",
        display: "block",
        minHeight: 100
      }
    };
  }

  close() {
    this.context.router.push(
      `/event_buckets/${this.props.params.eventBucketId}/settings`
    );
  }

  render() {
    let styles = this.getStyles(),
      bucket = this.props.getEventBucketById(this.props.params.eventBucketId);

    let query =
      this.state.defaultPage === "schedule"
        ? `?viewMode=${this.state.defaultViewMode}`
        : "";
    let path = this.state.defaultPage;

    return (
      <BaseOverlay
        title={"Embed wizard"}
        hideTitleAndFooterBorders={true}
        useMuiDefaultStyle={true}
        openImmediately={true}
        actions={[<FlatButton label={"Close"} onTouchTap={this.close} />]}
        modal={true}
        open={true}
        onRequestClose={this.close}
      >
        {!bucket ? (
          <WorkingIndicator fullPage={true} />
        ) : (
          <div>
            <InfoNotice style={{ marginBottom: 15, fontSize: 12 }}>
              Check out our{" "}
              <a
                href="http://help.eventive.org/integrations/embed-eventive-on-your-website"
                target="_blank"
              >
                guide to embedding Eventive
              </a>
              .
            </InfoNotice>
            Configure, then copy and paste the following HTML code where you'd
            like the embed to appear.
            <SelectField
              label="Page to embed"
              clearable={false}
              value={this.state.defaultPage}
              options={EMBED_PAGES}
              onChange={o => this.setState({ defaultPage: o.value })}
              style={{ width: 256 }}
            />
            {this.state.defaultPage === "schedule" ? (
              <div>
                <SelectField
                  label="Default embedded schedule view mode"
                  clearable={false}
                  value={this.state.defaultViewMode}
                  options={VIEW_MODES}
                  onChange={o => this.setState({ defaultViewMode: o.value })}
                  style={{ width: 256 }}
                />
              </div>
            ) : null}
            {this.state.defaultPage === "schedule" ||
            this.state.defaultPage === "films" ? (
              <div>
                <Label>Custom content</Label>
                <Checkbox
                  checked={this.state.showCustomContent}
                  label="Include custom content (set via Configurator) in embed"
                  style={{ marginTop: -5, marginLeft: -3 }}
                  labelStyle={{ fontSize: "0.8em", marginLeft: -10 }}
                  onCheck={(e, isChecked) => {
                    this.setState({ showCustomContent: isChecked });
                  }}
                />
              </div>
            ) : null}
            <div>
              <Label>Background color</Label>
              <Checkbox
                checked={this.state.alpha === 0}
                label="Transparent background"
                style={{ marginTop: -5, marginLeft: -3 }}
                labelStyle={{ fontSize: "0.8em", marginLeft: -10 }}
                onCheck={(e, isChecked) => {
                  this.setState({ alpha: isChecked ? 0 : 100 });
                }}
              />
              {this.state.alpha !== 0 ? (
                <div>
                  <span
                    style={{
                      verticalAlign: 8,
                      fontSize: "0.8em",
                      marginRight: 5
                    }}
                  >
                    Select color:
                  </span>
                  <ColorPicker
                    animation="slide-up"
                    color={this.state.color}
                    alpha={this.state.alpha}
                    defaultAlpha={this.state.alpha}
                    onChange={({ color, alpha }) => {
                      this.setState({ color, alpha });
                    }}
                  />
                </div>
              ) : null}
              {/*<Label>Text color</Label>
              <Checkbox
                checked={this.state.invertTextColor}
                label="Invert text color (for dark backgrounds)"
                style={{ marginTop: -5, marginLeft: -3 }}
                labelStyle={{ fontSize: "0.8em", marginLeft: -10 }}
                onCheck={(e, isChecked) => {
                  this.setState({ invertTextColor: isChecked });
                }}
              />*/}
            </div>
            <ClickToSelect>
              <pre style={styles.pre}>
                {`<a id="eventive-embed" href="${
                  bucket.urls.root
                }${path}${query}${
                  this.state.showCustomContent
                    ? `${
                        query.indexOf("?") === -1 ? "?" : "&"
                      }showCustomContent=true`
                    : ""
                }"${
                  this.state.invertTextColor ? ` data-text-color="#fff"` : ""
                }${
                  this.state.alpha !== 0
                    ? ` data-background-color="rgba(${this.state.colr
                        .fromHex(this.state.color)
                        .toRgbArray()
                        .join(", ")}, ${this.state.alpha / 100})"`
                    : ""
                }>View ${bucket.name} ${
                  EMBED_PAGES.find(p => p.value === path).inlineLabel
                }.</a><script type="text/javascript" src="${
                  bucket.urls.root
                }embed.js"></script>`}
              </pre>
            </ClickToSelect>
          </div>
        )}
      </BaseOverlay>
    );
  }
}

export default connectToStores(EventBucketEmbed);



// WEBPACK FOOTER //
// ./src/app/components/event-bucket-embed.jsx